<template>
  <div class="VisionCard__container">
    <component
      :is="props.isButton ? 'button' : props.link ? 'a' : 'div'"
      :href="props.link"
      :class="[
        'VisionCard',
        'VisionCard--align-' + props.alignment,
        'VisionCard--justify-' + props.justification,
        'VisionCard--ratio-' + props.aspectRatio,
        'VisionCard--colour-' + props.colour,
        'VisionCard--bg-gradient-' + props.bgGradient,
        {
          'VisionCard--has-promo-theme': props.hasPromoTheme,
          'VisionCard--full-height-content': props.fullHeightContent
        },
      ]"
      :style="`
        --scrim: ${props.scrim / 100};
        --aspect-ratio: ${props.aspectRatio};
        --bg-colour: ${props.bgColour};
        --alignment: ${props.alignment};
        --justification: ${props.justification};
        --text-colour: ${textColour};
    `"
      @click="emits('clicked')"
      @mouseover="handleHover(true)"
      @mouseleave="handleHover(false)"
    >
      <div
        class="VisionCard__content"
        :style="`
        background: ${gradient};
        filter: ${dropShadow};
      `"
      >
        <template v-if="$slots.content">
          <slot name="content" />
        </template>
        <template v-if="props.title">
          <VisionHeading
            :tag="props.tag"
            class="VisionCard__title"
          >
            {{ props.title }}
          </VisionHeading>
          <p class="VisionCard__description">
            {{ props.description }}
          </p>
        </template>
      </div>
      <div
        v-if="props.subtitle"
        :class="['VisionCard__subtitle-container',
                 {'VisionCard__subtitle-container--tag'
                   : props.subtitleStyle === 'tag'}
        ]"
      >
        <h6
          v-if="props.subtitleStyle === 'text'"
          class="VisionCard__subtitle"
        >
          {{ props.subtitle }}
        </h6>
        <VisionTag
          v-if="props.subtitleStyle === 'tag'"
          :text="props.subtitle"
          :theme="props.hasPromoTheme ? 'promo' : 'blur'"
          size="small"
        />
      </div>
      <div class="VisionCard__icon-button">
        <VisionIconButton
          v-if="showIconButton"
          :icon="iconType"
          :button-style="props.iconButton"
          :icon-colour="props.colour"
          :background-colour="props.colour === 'white' ? 'black' : 'white'"
          size="stretch"
          :parent-hover="hover"
          fake-button
          :has-promo-theme="props.hasPromoTheme"
        />
      </div>
      <div
        v-if="$slots.image"
        class="VisionCard__background"
      >
        <slot name="image" />
      </div>
    </component>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import VisionHeading from '../VisionHeading/VisionHeading.vue'
import VisionIconButton from '../VisionIconButton/VisionIconButton.vue'
import VisionTag from '../VisionTag/VisionTag.vue'

export type AspectRatio = '4/5' | '5/4' | '16/9' | '9/16' | '1/1' | 'auto'
export type Alignment = 'flex-start' | 'center' | 'flex-end'
export type Justification = 'left' | 'center'
export type TextColour = 'white' | 'black'
export type IconButtonStyle = 'outline' | 'fill' | 'none'
export type Theme = {
  id: string
  type?: 'default' | 'special'
  buttonBackgroundGradient?: string
  buttonBackgroundColour?: {
    hex?: string
  }
  buttonTextColour?: {
    hex?: string
  }
}

interface CardProps {
  content?: object[]
  aspectRatio?: AspectRatio | string
  bgColour?: string
  bgGradient?: 'default' | ''
  link?: string | null
  scrim?: number
  shadow?: number
  subtitle?: string
  subtitleStyle?: 'text' | 'tag'
  alignment?: Alignment
  justification?: Justification
  colour?: TextColour
  iconButton?: IconButtonStyle
  theme?: Theme | null
  title?: string
  description?: string
  tag?: string
  hasButton?: boolean
  isButton?: boolean
  hasPromoTheme?: boolean
  fullHeightContent?: boolean
}

const props = withDefaults(defineProps<CardProps>(), {
  content: () => [],
  bgColour: '#ffffff',
  bgGradient: '',
  aspectRatio: '4/5',
  link: '',
  scrim: 16,
  shadow: 0,
  subtitle: '',
  subtitleStyle: 'text',
  alignment: 'flex-end',
  justification: 'left',
  colour: 'white',
  iconButton: 'outline',
  theme: null,
  title: '',
  description: '',
  tag: 'h3',
  hasButton: false,
  isButton: false,
  hasPromoTheme: false,
  fullHeightContent: false
})

const emits = defineEmits(['clicked'])

const hover = ref<boolean>(false)

const gradient = computed(() => {
  const opacity = props.scrim / 100
  const direction = props.alignment === 'flex-start' ? 'to bottom' : 'to top'
  const rgbaStart =
    props.colour === 'white'
      ? `rgba(0,0,0,${opacity})`
      : `rgba(255,255,255,${opacity})`
  const rgbaEnd =
    props.colour === 'white' ? 'rgba(0,0,0,0)' : 'rgba(255,255,255,0)'
  if (props.alignment === 'center') return 'transparent'
  return `linear-gradient(${direction}, ${rgbaStart} 0%, ${rgbaEnd} 100%)`
})

const dropShadow = computed(() => {
  const opacity = props.shadow / 100
  const colour =
    props.colour === 'white'
      ? `rgba(0,0,0,${opacity})`
      : `rgba(255,255,255,${opacity})`
  return `drop-shadow(${colour} 0px 0px 2cqw)`
})

const iconType = computed(() => {
  if (props.isButton && !props.link)
    return 'plus'
  return 'arrow'
})

const showIconButton = computed(() => {
  if (props.hasButton) return false
  if (!props.link && !props.isButton) return false
  if (props.alignment === 'flex-end' && props.justification === 'center')
    return false
  if (props.iconButton === 'none') return false
  return true
})

const textColour = computed(() => {
  return props.colour === 'black' ? '#111111' : '#ffffff'
})

function handleHover(isHovered: boolean) {
  hover.value = isHovered
}
</script>

<style lang="postcss">
.VisionCard__container {
  container: card / inline-size;
}

.VisionCard {
  --vision-heading-size-override: var(--vision-card-title-size-override, var(--title-size));
  --vision-card-title-size-override: var(--vision-card-title-size);
  --vision-card-description-size-override: var(--vision-card-description-size);
  --vision-card-padding-override: var(--vision-card-padding);
  --vision-card-icon-button-width-override: var(--vision-card-icon-button-width);
  --vision-card-rounded-override: var(--vision-card-rounded);
  --rounded: 0.5rem;
  --title-size: 0.875rem;
  --description-size: 0.75rem;
  --subtitle-size: 0.625rem;
  --icon-width: 1.5rem;
  --icon-button-width: 1.5rem;
  --icon-button-display: hidden;
  --card-y-padding: 0.625rem;
  --card-x-padding: 0.75rem;
  --scrim-start: rgba(1, 0, 0, 0.56);
  --scrim-end: rgba(0, 0, 0, 0);
  background-color: var(--bg-colour);
  color: var(--text-colour);
  justify-content: var(--alignment);
  border-radius: var(--vision-card-rounded-override, var(--rounded));
  aspect-ratio: var(--aspect-ratio);

  @apply relative overflow-hidden
    flex flex-col w-full;

  & > * {
    @apply relative z-[2];
  }

  &:hover {
    .VisionCard__background {
      @apply scale-[1.03];
    }
  }
}

.VisionCard__content {
  @apply flex flex-col gap-0.5 w-full
    py-[var(--vision-card-padding-override,_var(--card-y-padding))]
    px-[var(--vision-card-padding-override,_var(--card-x-padding))];
  align-items: var(--justification);
  text-align: var(--justification);
  justify-content: var(--alignment);
}

.VisionCard__background {
  @apply absolute top-0 left-0 w-full h-full
    flex items-center justify-center
    transition-transform duration-300;
  z-index: 0;

  & > * {
    min-height: 100%;
    object-fit: cover;
  }
}

.VisionCard__subtitle-container {
  @apply absolute top-0 left-0 w-full flex
    py-[var(--vision-card-padding-override,_var(--card-y-padding))]
    px-[var(--vision-card-padding-override,_var(--card-x-padding))];
  justify-content: var(--justification);

  &--tag {
    @apply p-[var(--vision-card-padding-override,_var(--card-y-padding))]
  }
}

.VisionCard__subtitle {
  @apply opacity-[0.8] font-fragment-mono;
  font-size: var(--subtitle-size);
}

.VisionCard__title {
  @apply m-0;
}

.VisionCard__description {
  font-size: var(--vision-card-description-size-override, var(--description-size));
  @apply opacity-[0.8] m-0 font-light;
}

.VisionCard__icon-button {
  @apply absolute;
  width: var(--vision-card-icon-button-width-override, var(--icon-button-width));
  bottom: var(--vision-card-padding-override, var(--card-y-padding));
  right: var(--vision-card-padding-override, var(--card-x-padding));
  visibility: var(--icon-button-display);
}

/** Container Queries

 */
@container card (max-width: 180px) {
  .VisionCard {
    --icon-button-display: hidden;
    --icon-button-width: 1.25rem;
  }
}

 @container card (min-width: 181px) and (max-width: 240px) {
  .VisionCard {
    --icon-button-display: visible;
    --icon-button-width: 1.25rem;
  }
 }

@container card (min-width: 241px) {
  .VisionCard {
    --title-size: 1.125rem;
    --description-size: 0.875rem;
    --subtitle-size: 0.75rem;
    --icon-width: 3rem;
    --icon-button-width: 2rem;
  }

  /** Add padding to accommodate icon button above 240px */
  .VisionCard--align-flex-end:not(.VisionCard--justify-center)
  .VisionCard__content {
    padding-right: calc(var(--vision-card-padding-override, var(--card-y-padding)) * 2 + var(--vision-card-icon-button-width-override, var(--icon-button-width)));
  }
}

@container card (min-width: 361px) {
  .VisionCard {
    --card-y-padding: 1rem;
    --card-x-padding: 1.125rem;
    --title-size: 1.5rem;
    --description-size: 1.125rem;
    --subtitle-size: 0.875rem;
    --icon-width: 3.5rem;
    --icon-button-width: 2.5rem;
  }
}

@container card (min-width: 481px) {
  .VisionCard {
    --rounded: 0.75rem;
    --card-y-padding: 1.25rem;
    --card-x-padding: 1.5rem;
    --title-size: 2rem;
    --description-size: 1.25rem;
    --subtitle-size: 1rem;
    --icon-width: 4rem;
  }
}

@container card (min-width: 720px) {
  .VisionCard {
    --card-y-padding: 1.5rem;
    --card-x-padding: 1.75rem;
    --title-size: 2.5rem;
    --description-size: 1.5rem;
    --subtitle-size: 1.125rem;
    --icon-width: 5rem;
    --icon-button-width: 3rem;
  }
}

/**
  Modifiers
  --ratio
  --alignment
  --justification
  --colour
  --bg-gradient-{default}
  --has-promo-theme
  --full-height-content
 */

/** Ratio Auto */
.VisionCard--ratio-auto {
  @apply h-full;
}

/** Alignment */
.VisionCard--align-center::after {
  @apply content-[''] absolute inset-0
    bg-black bg-opacity-[var(--scrim)];
}

.VisionCard--align-flex-start .VisionCard__subtitle-container {
  @apply top-auto bottom-0;
}

.VisionCard--align-flex-end:not(.VisionCard--justify-center)
  .VisionCard__content {
  padding-right: var(--vision-card-padding-override, var(--card-y-padding));

  @container card (min-width: 241px) {
    padding-right: calc(var(--vision-card-padding-override, var(--card-y-padding)) * 2 + var(--vision-card-icon-button-width-override, var(--icon-button-width)));
  }
}

/** Colour */
.VisionCard--colour-black.VisionCard--align-center::after {
  @apply bg-white bg-opacity-[var(--scrim)];
}

/** Promo Specific Theme */
.VisionCard--has-promo-theme {
  .VisionCard__title {
    @apply promo-t-gradient promo-t-gradient-animated
  }
}

/** Background Gradient */
.VisionCard--bg-gradient-default {
  background: var(--gradient-default);
}

/** Ful Height Content */
.VisionCard--full-height-content {
  .VisionCard__content {
    @apply h-full;
  }
}
</style>
